import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import api from '../../api';
import './SignupScreen.css';

const SignupScreen = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    nickname: '',
    tosAccepted: false,
    website: ''  // honeypot — never filled by real users
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState(false);
  const [turnstileKey, setTurnstileKey] = useState(0); // increment to remount widget on retry
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();

  // If Turnstile hasn't resolved within 12 s the script likely failed to load
  // (most commonly caused by a CSP violation or a network/ad-blocker block).
  // Show an actionable error so the user isn't left staring at a grey button.
  useEffect(() => {
    if (turnstileToken) return;
    const timer = setTimeout(() => setTurnstileError(true), 12000);
    return () => clearTimeout(timer);
  }, [turnstileKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTurnstileRetry = () => {
    setTurnstileError(false);
    setTurnstileToken('');
    setTurnstileKey(k => k + 1);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    if (!formData.username || !formData.email || !formData.password) {
      setError('Username, email, and password are required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!formData.tosAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/auth/signup', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        nickname: formData.nickname || formData.username,
        tos_accepted: true,
        website: formData.website,
        cf_turnstile_response: turnstileToken
      });

      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="signup-screen">
        <div className="signup-container">
          <div className="signup-header">
            <h1>Account Created!</h1>
          </div>
          <div className="success-content">
            <p>We've sent a verification email to:</p>
            <p className="email-highlight">{formData.email}</p>
            <p>Please check your inbox and click the verification link to activate your account.</p>
            <div className="success-actions">
              <button 
                className="login-button"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-screen">
      <div className="signup-container">
        <div className="signup-header">
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: '10px' }}>
            <img
              src="/AmpNebulaSmall2.jpg"
              alt="AmpNebula"
              style={{ width: '200px', height: 'auto', borderRadius: '8px' }}
            />
            <span style={{
              position: 'absolute',
              top: '27%',
              right: '1%',
              fontSize: '0.5rem',
              color: 'rgba(255,255,255,0.8)',
              fontWeight: '600',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              lineHeight: 1
            }}>™</span>
          </div>
          <p>Create your account</p>
        </div>
        
        <form onSubmit={handleSubmit} className="signup-form" autoComplete="off">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              disabled={loading}
              autoFocus
              autoComplete="off"
              placeholder="Choose a username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
              autoComplete="off"
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="nickname">Display Name</label>
            <input
              type="text"
              id="nickname"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              disabled={loading}
              placeholder="How you want to be called (optional)"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={loading}
              autoComplete="new-password"
              placeholder="Confirm your password"
            />
          </div>
          
          {/* Honeypot — hidden from real users, filled only by bots */}
          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={handleChange}
            aria-hidden="true"
            tabIndex="-1"
            autoComplete="off"
            style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', opacity: 0 }}
          />

          <div className="form-group tos-checkbox-group">
            <label className="tos-checkbox-label">
              <input
                type="checkbox"
                name="tosAccepted"
                checked={formData.tosAccepted}
                onChange={handleChange}
                disabled={loading}
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
              </span>
            </label>
          </div>

          <Turnstile
            key={turnstileKey}
            siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
            onSuccess={(token) => { setTurnstileToken(token); setTurnstileError(false); }}
            onExpire={() => setTurnstileToken('')}
            onError={() => { setTurnstileToken(''); setTurnstileError(true); }}
            options={{ theme: 'auto', appearance: 'interaction-only' }}
          />

          {turnstileError && !turnstileToken && (
            <div className="error-message" style={{ marginBottom: '12px' }}>
              Security check failed or timed out.{' '}
              <button type="button" className="resend-button" onClick={handleTurnstileRetry}>
                Try again
              </button>
              {'. If the problem persists, try disabling browser extensions or refreshing the page.'}
            </div>
          )}

          <button 
            type="submit" 
            className="signup-button"
            disabled={
              loading ||
              !turnstileToken ||
              !formData.username.trim() ||
              !formData.email.trim() ||
              !formData.password ||
              !formData.confirmPassword ||
              !formData.tosAccepted
            }
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
          
          <div className="login-link">
            Already have an account? <Link to="/login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupScreen;
